import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import useForm from '@afosto/utils-react/validation/useForm';
import useIntl from '@afosto/utils-react/intl/useIntl';
import useRegisterInput from '@afosto/app-components-react/Form/useRegisterInput';
import { FormattedMessage } from 'react-intl';
import { reach } from 'yup';
import Button from '@material-ui/core/Button';
import Collapse from '@material-ui/core/Collapse';
import InputAdornment from '@material-ui/core/InputAdornment';
import ContactSearch from '@afosto/app-components-react/ContactSearch';
import FileUpload from '@afosto/app-components-react/FileUpload';
import Form from '@afosto/app-components-react/Form';
import FormActions from '@afosto/app-components-react/Form/FormActions';
import LoadingWrapper from '@afosto/components-react/LoadingWrapper';
import TextEditor from '@afosto/app-components-react/TextEditor';
import TextField from '@afosto/app-components-react/TextField';
import { files as filesApi } from '@afosto/content/api/Files';
import styled from 'styled-components';
import validationSchema from './validations';
import translations from './translations';

const FieldAction = styled(Button)`
  && {
    min-width: 42px;
  }
`;

const FileUploadContainer = styled.div`
  margin-top: ${props => props.theme.spacing(2)}px;
  margin-bottom: ${props => props.theme.spacing()}px;
`;

const EditorContainer = styled.div`
  margin-top: ${props => props.theme.spacing(2)}px;
  margin-bottom: ${props => props.theme.spacing()}px;

  .ql-editor {
    min-height: ${props => props.theme.spacing(30)}px;
  }

  .ql-toolbar {
    border-top-left-radius: ${props => props.theme.shape.borderRadius}px;
    border-top-right-radius: ${props => props.theme.shape.borderRadius}px;
  }

  .ql-container {
    border-bottom-left-radius: ${props => props.theme.shape.borderRadius}px;
    border-bottom-right-radius: ${props => props.theme.shape.borderRadius}px;
  }
`;

const EmailForm = ({ onSubmit, onCancel, email, isSavingEmail, autoFocusFirstField }) => {
  const intl = useIntl();
  const [showCcField, setShowCcField] = useState(false);
  const [showBccField, setShowBccField] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [files, setFiles] = useState([]);
  const defaultValues = {
    subject: email?.subject ?? '',
    content: email?.html_body ?? '',
    to: (email?.to || []).map(toContact => toContact.email),
    cc: (email?.cc || []).map(ccContact => ccContact.email),
    bcc: (email?.bcc || []).map(bccContact => bccContact.email),
    attachments: email?.attachments ?? [],
    folder: email?.folder ?? 'sent',
  };
  const form = useForm({
    validationSchema,
    defaultValues,
    onSubmit,
    onCancel,
    clearOnCancel: false,
  });
  const { setValue, watch } = form;
  const defaultAttachmentsString = JSON.stringify(defaultValues.attachments);

  const bccFieldValue = watch('bcc');
  const ccFieldValue = watch('cc');
  const bccLength = bccFieldValue.length;
  const ccLength = ccFieldValue.length;

  const handleCreateAttachment = file => {
    setAttachments(oldAttachments => [...oldAttachments, file.id]);
  };

  const handleRemoveAttachment = attachmentID => {
    setAttachments(oldAttachments =>
      oldAttachments.filter(currentAttachmentID => currentAttachmentID !== attachmentID),
    );
  };

  const fetchFileAttachments = async attachmentIds => {
    try {
      const promises = attachmentIds.map(attachmentId => filesApi.getFile(attachmentId));
      const response = await Promise.all(promises);

      const formattedFiles = response.reduce((acc, current) => {
        const { data } = current;

        return [
          ...acc,
          {
            source: data.id,
            options: {
              type: 'local',
              file: {
                name: data.filename,
                size: data.metadata.size,
                type: data.mime,
              },
            },
          },
        ];
      }, []);

      setFiles(formattedFiles);
    } catch (err) {
      // Do something with error.
      setFiles([]);
    }
  };

  useRegisterInput('attachments', true, form);

  useEffect(() => {
    setValue('attachments', attachments);
  }, [setValue, attachments]);

  useEffect(() => {
    if (ccLength > 0) {
      setShowCcField(true);
    }
  }, [ccLength]);

  useEffect(() => {
    if (bccLength > 0) {
      setShowBccField(true);
    }
  }, [bccLength]);

  useEffect(() => {
    const defaultAttachments = JSON.parse(defaultAttachmentsString);

    setAttachments(defaultAttachments);
    fetchFileAttachments(defaultAttachments);
  }, [defaultAttachmentsString]);

  const handleChangeEmail = name => async (ev, newValue) => {
    try {
      const emails = newValue.reduce((acc, value) => {
        if (typeof value === 'object') {
          return [...acc, value.email];
        }

        return [...acc, value];
      }, []);

      if (newValue.length !== 0) {
        await reach(validationSchema, name).validate(emails);
      }

      setValue(name, emails);
    } catch (err) {
      // Do nothing
    }
  };

  return (
    <LoadingWrapper isLoading={isSavingEmail || form.isSubmitting}>
      <Form form={form} renderActions={false}>
        <ContactSearch
          name="to"
          label={intl.formatMessage(translations.fields.to.label)}
          multiple
          enableCreate
          filterSelectedOptions
          autoFocus={autoFocusFirstField}
          onChange={handleChangeEmail('to')}
          InputProps={
            !showBccField || !showCcField
              ? {
                  endAdornment: (
                    <InputAdornment position="end">
                      {!showCcField && (
                        <FieldAction
                          edge="end"
                          size="small"
                          color="primary"
                          onClick={() => setShowCcField(!showCcField)}
                        >
                          <FormattedMessage {...translations.fields.cc.label} />
                        </FieldAction>
                      )}
                      {!showBccField && (
                        <FieldAction
                          edge="end"
                          size="small"
                          color="primary"
                          onClick={() => setShowBccField(!showBccField)}
                        >
                          <FormattedMessage {...translations.fields.bcc.label} />
                        </FieldAction>
                      )}
                    </InputAdornment>
                  ),
                }
              : undefined
          }
        />

        <Collapse in={showCcField}>
          <ContactSearch
            name="cc"
            label={intl.formatMessage(translations.fields.cc.label)}
            multiple
            enableCreate
            filterSelectedOptions
            onChange={handleChangeEmail('cc')}
          />
        </Collapse>

        <Collapse in={showBccField}>
          <ContactSearch
            name="bcc"
            label={intl.formatMessage(translations.fields.bcc.label)}
            multiple
            enableCreate
            filterSelectedOptions
            onChange={handleChangeEmail('bcc')}
          />
        </Collapse>

        <TextField name="subject" label={intl.formatMessage(translations.fields.subject.label)} />

        <EditorContainer>
          <TextEditor name="content" />
        </EditorContainer>

        <FileUploadContainer>
          <FileUpload
            allowMultiple
            onUploadFile={handleCreateAttachment}
            onRemoveFile={handleRemoveAttachment}
            onRevertFile={handleRemoveAttachment}
            files={files}
            allowFileSizeValidation
            maxTotalFileSize="8MB"
            uploadPath="/emails/attachments"
            uploadMethod="resolve"
          />
        </FileUploadContainer>
        <FormActions
          primaryAction={{
            label: intl.formatMessage(translations.formActions.send),
            onClick: form.onSubmit,
            type: 'submit',
            color: 'primary',
            variant: 'contained',
            disabled: isSavingEmail,
          }}
          secondaryAction={{
            label: intl.formatMessage(translations.formActions.cancel),
            onClick: form.onCancel,
            disabled: isSavingEmail,
          }}
        />
      </Form>
    </LoadingWrapper>
  );
};

EmailForm.propTypes = {
  autoFocusFirstField: PropTypes.bool,
  onSubmit: PropTypes.func,
  onCancel: PropTypes.func,
  email: PropTypes.object,
  isSavingEmail: PropTypes.bool,
};

EmailForm.defaultProps = {
  autoFocusFirstField: true,
  onSubmit: undefined,
  onCancel: undefined,
  email: null,
  isSavingEmail: false,
};

export default EmailForm;
